import { escapeHtml } from "../../../utils/escapeHtml";
import { serverMessage } from "../../../utils/serverMessage";

// Contact import (QA batch 15, CONT-033 / CONT-038): only CSV files work, and
// the server reports rows it skipped (backend `Services/ContactImport.cs`).

export const IMPORT_CSV_ONLY =
  "Only CSV files can be imported. In Excel choose File > Save As > \"CSV (Comma delimited)\" and import that file.";

export const IMPORT_FILE_TYPES_HINT = "CSV files only (.csv).";

export const importFileProblem = (fileName: string | undefined): string =>
  /\.csv$/i.test((fileName || "").trim()) ? "" : IMPORT_CSV_ONLY;

export type ContactImportResult = {
  imported?: number;
  skipped?: { row: number; name?: string; reason: string }[];
};

/** Rows listed in the result window; the rest are counted. */
export const MAX_LISTED_SKIPPED = 8;

/**
 * Title and description (HTML for SettingsConfirmModal; all file text is
 * escaped) of the window shown after an import.
 */
export const importResultMessage = (
  result: ContactImportResult | null | undefined,
  variant: "individual" | "organization",
): { title: string; description: string; ok: boolean } => {
  const imported = result?.imported ?? 0;
  const skipped = result?.skipped ?? [];
  const noun = variant === "individual" ? "contact" : "organization";
  const importedText = `${imported} ${noun}${
    imported === 1 ? "" : "s"
  } imported.`;
  if (!skipped.length) {
    return {
      title: imported ? "Import finished" : "Nothing was imported",
      description: imported ? importedText : "The file had no rows to import.",
      ok: imported > 0,
    };
  }
  const listed = skipped
    .slice(0, MAX_LISTED_SKIPPED)
    .map(
      (row) =>
        `Row ${row.row}${
          row.name ? ` (${escapeHtml(row.name)})` : ""
        }: ${escapeHtml(row.reason)}`,
    );
  const more = skipped.length - listed.length;
  return {
    title: imported
      ? "Import finished with skipped rows"
      : "Nothing was imported",
    description: [
      importedText,
      `${skipped.length} row${
        skipped.length === 1 ? " was" : "s were"
      } skipped:`,
      ...listed,
      ...(more > 0 ? [`…and ${more} more.`] : []),
      "Fix these rows in the file and import only them again.",
    ].join("<br />"),
    ok: imported > 0,
  };
};

/**
 * The server's sentence for a refused file (400), else a general message.
 * CONT-038 (QA batch 17): the server answers the 400 as plain text, which RTK
 * Query reports as "PARSING_ERROR" (real status in originalStatus), so the
 * sentence was never shown. `serverMessage` reads both shapes.
 */
export const importErrorMessage = (error: unknown): string =>
  serverMessage(error, "The import didn't work. Check the file and try again.");
